import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContentPanelProps } from '@/types/predefined-messages';
import type { MappedRoleOption } from '@/types/roles';

import { CategoryForm } from '@/components/configuration-modal/category-form';
import { ContentPanelHeader } from '@/components/configuration-modal/content-panel-header/content-panel-header';
import { PredefinedMessageForm } from '@/components/configuration-modal/predefined-message-form';
import { PredefinedMessagesFilters } from '@/components/configuration-modal/predefined-messages-filters';
import { PredefinedMessagesTable } from '@/components/configuration-modal/predefined-messages-table';
import { ALL } from '@/constants';
import { useCategoryForms } from '@/hooks/use-category-forms';
import { usePredefinedMessageFilters } from '@/hooks/use-predefined-message-filters';
import { usePredefinedMessageForms } from '@/hooks/use-predefined-message-forms';
import { createLookup } from '@/lib/helpers/create-lookup';
import { getRoleDisplayName } from '@/lib/helpers/role-mapper';
import { NOTIFIABLE_ROLES, ROLES } from '@/utils/constants/roles';

export function ContentPanel({
  selectedCategoryId,
  onSelectCategoryId,
  messages,
  categories,
  categoryForm,
  setCategoryForm,
}: ContentPanelProps) {
  const { t } = useTranslation();
  const [localMessages, setLocalMessages] = useState(messages);

  const mappedRoleList: MappedRoleOption[] = Object.entries(ROLES).map(
    ([key, { id, name, shortName }]) => ({
      id,
      key,
      name,
      shortName,
    })
  );

  const { showCategoryForm, categoryFormConditionalProps, openAddCategoryForm, closeCategoryForm } =
    useCategoryForms({
      selectedCategoryId,
      onSelectCategoryId,
      categories,
      categoryForm,
      setCategoryForm,
    });

  const {
    showMessageForm,
    messageFormKey,
    messageFormMode,
    messageFormInitialValues,
    defaultCategoryForForm,
    openAddMessageForm,
    closeMessageForm,
    submitMessageForm,
    onEditingMessage,
    deleteMessage,
  } = usePredefinedMessageForms({
    selectedCategoryId,
    messages: localMessages,
    setMessages: setLocalMessages,
    categories,
    categoryForm,
    setCategoryForm,
  });

  const categoryMessages = useMemo(
    () =>
      selectedCategoryId === ALL
        ? localMessages
        : localMessages.filter(
            ({ predefinedMessageCategoryId }) => predefinedMessageCategoryId === selectedCategoryId
          ),
    [selectedCategoryId, localMessages]
  );

  const sortedRoleOptions = useMemo(() => {
    const allRole = mappedRoleList.find(({ key }) => key === 'all');
    const otherRoles = mappedRoleList
      .filter(({ key }) => key !== 'all')
      .sort((a, b) => a.name.localeCompare(b.name));

    return allRole ? [allRole, ...otherRoles] : otherRoles;
  }, [mappedRoleList]);

  const roleFilterOptions = useMemo(
    () =>
      sortedRoleOptions.map(({ key, name, shortName }) => ({
        id: key,
        name,
        shortName,
        label: `${getRoleDisplayName(shortName, t)} (${getRoleDisplayName(name, t)})`,
      })),
    [sortedRoleOptions, t]
  );

  const recipientRoleOptions = useMemo(
    () => roleFilterOptions.filter(({ id }) => id in NOTIFIABLE_ROLES),
    [roleFilterOptions]
  );

  const categoryNames = useMemo(
    () => createLookup(categories, 'id', ({ name }) => name),
    [categories]
  );

  const { filters, filteredMessages } = usePredefinedMessageFilters(categoryMessages);

  return (
    <div className="flex flex-1 flex-col px-6 pt-6">
      <ContentPanelHeader onAddMessage={openAddMessageForm} onAddCategory={openAddCategoryForm} />
      {showCategoryForm && (
        <CategoryForm
          key={categoryForm}
          onSuccess={closeCategoryForm}
          onCancel={closeCategoryForm}
          {...categoryFormConditionalProps}
        />
      )}
      {showMessageForm && (
        <PredefinedMessageForm
          key={messageFormKey}
          mode={messageFormMode}
          categories={categories}
          roleFilterOptions={roleFilterOptions}
          recipientRoleOptions={recipientRoleOptions}
          defaultCategoryId={defaultCategoryForForm}
          initialValues={messageFormInitialValues}
          onSubmit={submitMessageForm}
          onCancel={closeMessageForm}
        />
      )}
      <PredefinedMessagesFilters
        filters={filters}
        roleFilterOptions={roleFilterOptions}
        recipientRoleOptions={recipientRoleOptions}
      />
      <div className="mt-4 flex min-h-0 min-w-0 flex-1 flex-col">
        <PredefinedMessagesTable
          className="border-train-stroke rounded-t-lg border pt-2"
          filteredMessages={filteredMessages}
          categoryNames={categoryNames}
          recipients={sortedRoleOptions}
          onEdit={onEditingMessage}
          onDelete={deleteMessage}
        />
      </div>
    </div>
  );
}
